import React from 'react';
import { TestimonialCard } from './TestimonialCard';
import { VideoTestimonialCard } from './VideoTestimonialCard';
import { CTAButton } from './CTAButton';
import { CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { TestimonialCardWithImage } from './TestimonialCardWithImage';
export function Advertorial() {
  return <div className="max-w-3xl mx-auto px-4 py-6 font-sans" data-id="element-5">
      {/* Main Headline */}
      <h1 className="text-3xl md:text-4xl font-bold text-center text-purple-900 mb-4" data-id="element-6">
        The Hidden Reason Your Dog Can't Stop Scratching
        <span className="block text-2xl md:text-3xl mt-2 text-gray-700" data-id="element-7">
          (And Why Everything You've Tried Has Failed)
        </span>
      </h1>
      {/* Subheading */}
      <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-700 mb-8" data-id="element-8">
        An Important Message for Dog Parents Who Are Tired of Watching Their
        Best Friend Suffer
      </h2>
      {/* Hero Image */}
      <div className="mb-8 text-center" data-id="element-9">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ehJLcLkDNunZkvUF4T7eNk/Frame_182_1.webp" alt="Before and after comparison of a dog with skin condition" className="rounded-lg shadow-lg max-w-full md:max-w-lg mx-auto border-2 border-gray-100 hover:border-purple-100 transition-colors duration-300" data-id="element-10" />
      </div>
      {/* Introduction */}
      <div className="space-y-6 mb-8" data-id="element-11">
        <p className="text-lg mb-4" data-id="element-12">
          If you're reading this at 3am because your dog's scratching woke you
          up again...
        </p>
        <p className="text-lg mb-4" data-id="element-13">
          If you've watched helplessly as your dog chewed their paws raw,
          leaving bloody spots on your floors...
        </p>
        <p className="text-lg mb-4" data-id="element-14">
          If you've spent hundreds (maybe thousands) on vet visits, special
          shampoos, and "miracle" supplements that promised to stop the itching
          but barely made a dent...
        </p>
        <p className="text-lg font-bold mb-8" data-id="element-15">
          Then what I'm about to share might be the most important thing you
          read all year.
        </p>
        <p className="text-lg mb-5" data-id="element-16">
          But first, I need you to know:{' '}
          <span className="font-bold" data-id="element-17">This isn't your fault.</span>
        </p>
        <p className="text-lg mb-5" data-id="element-18">
          You've done everything right. You've followed your vet's advice.
          You've tried the prescription medications. You've switched foods more
          times than you can count.
        </p>
        <p className="text-lg mb-6" data-id="element-19">
          And still, your dog scratches. And scratches. And scratches.
        </p>
      </div>
      {/* Problem Section */}
      <div className="mb-10" data-id="element-20">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-21">
          The Heartbreaking Cycle Every Dog Parent Knows Too Well
        </h2>
        <p className="text-lg mb-4" data-id="element-22">Let me guess how it started...</p>
        <p className="text-lg mb-4" data-id="element-23">
          One day, you noticed your dog scratching more than usual. Maybe
          licking their paws. You figured it was just seasonal allergies. No big
          deal.
        </p>
        <p className="text-lg mb-4" data-id="element-24">But it got worse.</p>
        <p className="text-lg mb-4" data-id="element-25">
          The scratching became constant. The licking turned obsessive. You
          started finding clumps of fur on the carpet. Hot spots appeared. Your
          once-happy dog seemed miserable, and you felt that gut-wrenching guilt
          that only a true dog parent understands.
        </p>
        <p className="text-lg mb-4" data-id="element-26">
          So you did what any loving guardian would do—you went to the vet.
        </p>
        <p className="text-lg mb-4" data-id="element-27">
          And that's when the expensive nightmare began.
        </p>
      </div>
      {/* The False Solution */}
      <div className="mb-10" data-id="element-28">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-29">
          The "Solution" That Isn't Really a Solution
        </h2>
        <p className="text-lg mb-4" data-id="element-30">
          Your vet probably took one look and prescribed Apoquel. Or maybe
          steroids. They assured you it would help with the itching.
        </p>
        <p className="text-lg mb-4" data-id="element-31">And maybe it did... for a while.</p>
        <p className="text-lg mb-4" data-id="element-32">
          But then you Googled the side effects (because of course you did—you
          research everything for your baby). What you found made your stomach
          drop:
        </p>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-33">
          <div className="flex" data-id="element-34">
            <div className="flex-shrink-0" data-id="element-35">
              <div className="h-5 w-5 text-red-500" data-id="element-36" />
            </div>
            <div className="ml-3" data-id="element-37">
              <p className="text-red-700" data-id="element-38">
                "May increase the chances of developing serious infections..."
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-39">
          <div className="flex" data-id="element-40">
            <div className="flex-shrink-0" data-id="element-41">
              <div className="h-5 w-5 text-red-500" data-id="element-42" />
            </div>
            <div className="ml-3" data-id="element-43">
              <p className="text-red-700" data-id="element-44">
                "May cause existing parasitic skin infestations or pre-existing
                cancers to get worse..."
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-45">
          <div className="flex" data-id="element-46">
            <div className="flex-shrink-0" data-id="element-47">
              <div className="h-5 w-5 text-red-500" data-id="element-48" />
            </div>
            <div className="ml-3" data-id="element-49">
              <p className="text-red-700" data-id="element-50">
                "Common side effects include vomiting, diarrhea, lethargy..."
              </p>
            </div>
          </div>
        </div>
        <p className="text-lg mb-4" data-id="element-51">
          You're stuck between watching your dog suffer from the itching or
          potentially poisoning them with pharmaceuticals. Some choice, right?
        </p>
        <p className="text-lg mb-4" data-id="element-52">
          But here's what really makes my blood boil...
        </p>
      </div>
      {/* The Real Problem */}
      <div className="mb-10" data-id="element-53">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-54">
          Your Vet Never Told You WHY Your Dog Is Itching
        </h2>
        <p className="text-lg mb-4" data-id="element-55">
          Think about it. When you brought your dog in, scratching and
          miserable, did your vet ever sit you down and explain what was
          actually causing the inflammation?
        </p>
        <p className="text-lg mb-4" data-id="element-56">
          Did they mention that itchy skin is rarely just "allergies"—that it's
          actually your dog's body screaming that something deeper is wrong?
        </p>
        <p className="text-lg mb-4" data-id="element-57">Of course not.</p>
        <p className="text-lg mb-4" data-id="element-58">
          Because here's the dirty little secret: Most vets are trained to treat
          symptoms, not root causes. They're taught to reach for the
          prescription pad, not dig into why your dog's immune system is in
          overdrive.
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-59">
          They're putting a Band-Aid on a bullet wound.
        </p>
      </div>
      {/* The Real Culprit */}
      <div className="mb-10" data-id="element-60">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-61">
          The Real Culprit Has Been Hiding in Your Dog's Bowl This Whole Time
        </h2>
        <p className="text-lg mb-4" data-id="element-62">
          What if I told you that your dog's relentless itching isn't really
          about fleas, grass, or seasonal allergies?
        </p>
        <p className="text-lg mb-4" data-id="element-63">
          What if the real problem has been sitting in their food bowl every
          single day?
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-64">
          Here's the truth that the billion-dollar pet food industry doesn't
          want you to know:
        </p>
        <p className="text-xl font-bold text-center text-red-600 mb-6" data-id="element-65">
          Modern commercial dog food is making our dogs sick.
        </p>
        <div className="mb-6 text-center" data-id="element-66">
          <img src="https://images.unsplash.com/photo-1589924691995-400dc9ecc119?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" alt="Commercial dog food" className="rounded-lg shadow-md max-w-full md:max-w-lg mx-auto" data-id="element-67" />
        </div>
        <p className="text-lg mb-4" data-id="element-68">
          I know that sounds extreme. But stick with me for just a moment...
        </p>
        <p className="text-lg mb-4" data-id="element-69">
          Dogs evolved eating a diet that was 49% protein and only 6%
          carbohydrates. They thrived on fresh meat, organs, and the occasional
          plant matter from their prey's stomach.
        </p>
        <p className="text-lg mb-4" data-id="element-70">
          But look at the ingredient list on most kibble bags today. What do you
          see?
        </p>
        <p className="text-lg mb-4" data-id="element-71">Corn. Wheat. Soy. Potato. Rice. Peas.</p>
        <p className="text-lg mb-4" data-id="element-72">
          Cheap fillers that bulk up the food and bulk up Big Kibble's profits.
          The average kibble is now over 40% carbohydrates—nearly SEVEN TIMES
          what nature intended.
        </p>
        <p className="text-lg mb-4" data-id="element-73">
          Your dog's body literally doesn't know what to do with all those
          processed carbs and grains.
        </p>
        <p className="text-lg mb-4" data-id="element-74">
          So it rebels. It creates inflammation. And where does that
          inflammation show up first?
        </p>
        <p className="text-lg mb-4" data-id="element-75">The skin.</p>
        <p className="text-lg font-bold mb-4" data-id="element-76">
          That "mysterious" itching isn't so mysterious after all. It's your
          dog's check engine light—a warning that their entire system is
          inflamed and crying out for help.
        </p>
      </div>
      {/* Why Other Solutions Fail */}
      <div className="mb-10" data-id="element-77">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-78">
          Why Those Other Supplements Keep Failing You
        </h2>
        <p className="text-lg mb-4" data-id="element-79">
          Maybe you've already figured out that drugs aren't the answer. Good
          for you—you're ahead of the curve.
        </p>
        <p className="text-lg mb-4" data-id="element-80">
          So you've tried the natural route. The salmon oil. The probiotics.
          Those "vet-recommended" soft chews that smell like a dumpster and cost
          $40 a bottle.
        </p>
        <p className="text-lg mb-4" data-id="element-81">
          Some of them might have helped a little. Most probably did nothing.
          And I bet at least one made things worse (am I right?).
        </p>
        <p className="text-lg mb-4" data-id="element-82">Here's why they keep failing:</p>
        <p className="text-lg font-bold mb-4" data-id="element-83">
          They're still treating the symptom, not the cause.
        </p>
        <p className="text-lg mb-4" data-id="element-84">
          It's like trying to bail water out of a sinking boat without plugging
          the hole. Sure, omega-3s might calm inflammation temporarily.
          Probiotics might help a bit with gut health.
        </p>
        <p className="text-lg mb-4" data-id="element-85">
          But if you're still feeding your dog inflammatory ingredients every
          single day, you're fighting a losing battle.
        </p>
        <p className="text-lg mb-4" data-id="element-86">
          Those supplements can't overcome the damage being done by a
          fundamentally corrupted diet. They're overwhelmed before they even
          have a chance.
        </p>
        <p className="text-lg font-bold mt-8 mb-4" data-id="element-87">
          Plus, let's be honest about those "leading brands"...
        </p>
        <div className="space-y-6" data-id="element-88">
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400" data-id="element-89">
            <p className="text-lg mb-2" data-id="element-90">
              <span className="font-bold" data-id="element-91">Zesty Paws?</span> Great marketing,
              but dogs hate the taste and owners report scary side effects (one
              customer said their dog became "less coordinated, weak" and
              recovered only after stopping the supplement).
            </p>
          </div>
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400" data-id="element-92">
            <p className="text-lg mb-2" data-id="element-93">
              <span className="font-bold" data-id="element-94">PetHonesty?</span> Loaded with
              shellfish that can trigger the very allergies they claim to solve.
            </p>
          </div>
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400" data-id="element-95">
            <p className="text-lg mb-2" data-id="element-96">
              <span className="font-bold" data-id="element-97">VetriScience?</span> Just read the
              reviews: "Dog hated them and gave him really bad diarrhea. They
              smell so bad, so I don't blame him for not liking them."
            </p>
          </div>
        </div>
        <p className="text-lg mt-6 mb-4" data-id="element-98">
          These companies are doing the same thing Big Pharma does—throwing
          random ingredients at the problem and hoping something sticks. They're
          not addressing the fundamental breakdown in your dog's nutrition.
        </p>
        <p className="text-lg mb-4" data-id="element-99">But what if there was a different way?</p>
        <p className="text-lg font-bold mb-6" data-id="element-100">
          What if, instead of masking symptoms or adding more pills to the pile,
          you could actually give your dog's body what it's been desperately
          missing all along?
        </p>
      </div>
      {/* The Discovery */}
      <div className="mb-10" data-id="element-101">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-102">
          The Discovery That Changed Everything
        </h2>
        <div className="flex flex-col md:flex-row items-center mb-6" data-id="element-103">
          <div className="md:w-1/3 mb-4 md:mb-0 md:pr-6" data-id="element-104">
            <img src="https://images.unsplash.com/photo-1559190394-df5a28aab5c5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" alt="Dr. Sarah Chen with her dog Max" className="rounded-full w-48 h-48 object-cover mx-auto border-4 border-purple-200" data-id="element-105" />
          </div>
          <div className="md:w-2/3" data-id="element-106">
            <p className="text-lg mb-4" data-id="element-107">
              My name is Dr. Sarah Chen, and I need to tell you about Max.
            </p>
            <p className="text-lg mb-4" data-id="element-108">
              Max was my 8-year-old Golden Retriever, and he was falling apart
              before my eyes. The itching had gotten so bad that he'd rubbed his
              nose raw against the walls. His beautiful coat was patchy and
              dull. The spark in his eyes had dimmed.
            </p>
            <p className="text-lg mb-4" data-id="element-109">
              I'm a nutritional biologist, but at that moment, I felt like a
              complete failure. Here I was, someone who understood cellular
              health at a molecular level, and I couldn't even help my own dog.
            </p>
          </div>
        </div>
        <p className="text-lg mb-4" data-id="element-110">
          The breaking point came after another useless vet visit. Another
          prescription for Apoquel. Another lecture about "managing his
          allergies." Another $300 down the drain.
        </p>
        <p className="text-lg mb-4" data-id="element-111">
          That night, as I watched Max scratch himself bloody yet again,
          something inside me snapped.
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-112">
          If modern veterinary medicine couldn't help him, then I would find my
          own solution.
        </p>
        <h3 className="text-xl md:text-2xl font-bold text-purple-800 mt-8 mb-4" data-id="element-113">
          The Rabbit Hole That Led to a Revelation
        </h3>
        <p className="text-lg mb-4" data-id="element-114">
          I dove deep into research, but not where you'd expect. Instead of
          veterinary journals, I looked at evolutionary biology. Instead of pet
          nutrition studies funded by Purina, I examined what wild canines
          actually eat.
        </p>
        <p className="text-lg mb-4" data-id="element-115">What I discovered made everything click:</p>
        <p className="text-lg font-bold text-center text-purple-900 my-6" data-id="element-116">
          Modern dogs aren't suffering from mysterious "allergies." They're
          suffering from malnutrition.
        </p>
        <p className="text-lg mb-4" data-id="element-117">
          Not the kind where they're skinny and underfed. The kind where they're
          getting plenty of calories but ZERO of the ancestral nutrients their
          bodies are programmed to thrive on.
        </p>
        <p className="text-lg mb-4" data-id="element-118">
          Think about it: For thousands of years, dogs ate nose-to-tail. They
          got organ meats rich in compounds we can't even pronounce. They
          consumed bone marrow, cartilage, and fermented plant matter from their
          prey's stomach. Their bodies evolved to expect these nutrients.
        </p>
        <p className="text-lg mb-4" data-id="element-119">
          Then, about 70 years ago, we started feeding them... brown cardboard
          pellets.
        </p>
        <p className="text-lg mb-4" data-id="element-120">
          We stripped away 99% of the nutritional complexity their bodies need
          and replaced it with processed corn and "meat meal."
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-121">
          No wonder they're falling apart.
        </p>
        <h3 className="text-xl md:text-2xl font-bold text-purple-800 mt-8 mb-4" data-id="element-122">
          The Ancestral Nutrient Reboot That Saved Max (And Now Thousands of
          Other Dogs)
        </h3>
        <p className="text-lg mb-4" data-id="element-123">
          I started sourcing ingredients you won't find in any pet store:
        </p>
        <ul className="list-none mb-6 space-y-3" data-id="element-124">
          <li className="flex items-start" data-id="element-125">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-126" />
            <span className="text-lg" data-id="element-127">
              <span className="font-bold" data-id="element-128">Grass-fed beef organs</span> - Liver,
              kidney, and heart containing nutrients that vanished from
              commercial dog food decades ago
            </span>
          </li>
          <li className="flex items-start" data-id="element-129">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-130" />
            <span className="text-lg" data-id="element-131">
              <span className="font-bold" data-id="element-132">Wild-caught green-lipped mussel</span>{' '}
              - With natural compounds that cool inflammation at the cellular
              level
            </span>
          </li>
          <li className="flex items-start" data-id="element-133">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-134" />
            <span className="text-lg" data-id="element-135">
              <span className="font-bold" data-id="element-136">Targeted botanicals</span> like
              turmeric and boswellia - Used for centuries before Big Pharma
              existed
            </span>
          </li>
          <li className="flex items-start" data-id="element-137">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-138" />
            <span className="text-lg" data-id="element-139">
              <span className="font-bold" data-id="element-140">Soil-based probiotics</span> - The
              kind dogs would naturally get from digging and hunting, not the
              fragile lab-grown strains that die in your cabinet
            </span>
          </li>
        </ul>
        <p className="text-lg mb-4" data-id="element-141">
          But here's the crucial part: It wasn't enough to just throw these
          ingredients together. I needed to recreate the synergy of an ancestral
          diet—where nutrients work together in ways science is only beginning
          to understand.
        </p>
        <p className="text-lg mb-4" data-id="element-142">
          It took me 18 months and dozens of formulations. Max was my very
          patient (and itchy) test subject.
        </p>
        <p className="text-lg mb-4" data-id="element-143">
          Then, on day 12 of formula #37, something incredible happened.
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-144">
          Max slept through the night. No scratching. No licking. For the first
          time in two years.
        </p>
        <p className="text-lg mb-4" data-id="element-145">
          By week 3, the hot spots were healing. By week 6, his coat was growing
          back thicker than ever. By week 12, he was playing like a puppy
          again—at 8 years old.
        </p>
        <p className="text-lg mb-4" data-id="element-146">
          But the best part? When I took him for his next check-up, my vet's jaw
          literally dropped. "What did you do?" she asked. "His inflammation
          markers are better than dogs half his age."
        </p>
        <h3 className="text-xl md:text-2xl font-bold text-purple-800 mt-8 mb-4" data-id="element-147">
          When Word Spread, Everything Changed
        </h3>
        <p className="text-lg mb-4" data-id="element-148">
          I started sharing my formula with friends at the dog park. Just a few
          fellow desperate dog parents who'd tried everything else.
        </p>
        <p className="text-lg mb-4" data-id="element-149">The messages started pouring in:</p>
        <div className="bg-purple-50 p-5 rounded-lg mb-4 border-l-4 border-purple-300" data-id="element-150">
          <p className="text-lg italic" data-id="element-151">
            "Sophie stopped scratching after just one week. I actually cried."
          </p>
        </div>
        <div className="bg-purple-50 p-5 rounded-lg mb-4 border-l-4 border-purple-300" data-id="element-152">
          <p className="text-lg italic" data-id="element-153">
            "We canceled Buddy's dermatology appointment. We don't need it
            anymore."
          </p>
        </div>
        <div className="bg-purple-50 p-5 rounded-lg mb-5 border-l-4 border-purple-300" data-id="element-154">
          <p className="text-lg italic" data-id="element-155">
            "I thought we'd have to put Luna on Apoquel forever. This saved us."
          </p>
        </div>
        <p className="text-lg mb-4" data-id="element-156">
          That's when I knew I couldn't keep this to myself. Too many dogs were
          suffering needlessly. Too many owners were being failed by a system
          that profits from keeping pets sick.
        </p>
        <p className="text-lg mb-4" data-id="element-157">
          So I partnered with a small, family-owned manufacturer who agreed to
          source every ingredient exactly as I specified. No shortcuts. No
          compromises.
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-158">
          And The Daily Foundation Chew was born.
        </p>
      </div>
      {/* Why It Works - MOVED HERE */}
      <div className="mb-10" data-id="element-159">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4" data-id="element-160">
          Why This Works When Everything Else Failed
        </h2>
        <p className="text-lg mb-4" data-id="element-161">
          The Daily Foundation Chew isn't another "itch supplement." It's not a
          multivitamin with a fancy marketing budget.
        </p>
        <p className="text-lg font-bold mb-4" data-id="element-162">
          It's a cellular reboot for your dog's entire system.
        </p>
        <p className="text-lg mb-4" data-id="element-163">
          Here's what happens when your dog gets these ancestral nutrients
          consistently:
        </p>
        <div className="bg-purple-50 p-6 rounded-lg mb-6" data-id="element-164">
          <h3 className="font-bold text-xl mb-2 text-purple-800" data-id="element-165">Week 1-2:</h3>
          <p className="text-lg" data-id="element-166">
            The inflammatory fire starts to cool. Many dogs show reduced itching
            within days as their body finally gets the tools it needs to calm
            the immune response.
          </p>
        </div>
        <div className="bg-purple-50 p-6 rounded-lg mb-6" data-id="element-167">
          <h3 className="font-bold text-xl mb-2 text-purple-800" data-id="element-168">Week 3-4:</h3>
          <p className="text-lg" data-id="element-169">
            Gut health improves dramatically. (Remember, 70% of the immune
            system lives in the gut.) Better digestion means better nutrient
            absorption and less systemic inflammation.
          </p>
        </div>
        <div className="bg-purple-50 p-6 rounded-lg mb-6" data-id="element-170">
          <h3 className="font-bold text-xl mb-2 text-purple-800" data-id="element-171">Week 5-8:</h3>
          <p className="text-lg" data-id="element-172">
            The visible transformation. Coat regrows. Energy returns. That
            sparkle comes back to their eyes. This is when other dog parents
            start asking, "What are you doing differently?"
          </p>
        </div>
        <div className="bg-purple-50 p-6 rounded-lg mb-6" data-id="element-173">
          <h3 className="font-bold text-xl mb-2 text-purple-800" data-id="element-174">
            Week 12 and beyond:
          </h3>
          <p className="text-lg" data-id="element-175">
            Deep, cellular-level healing. Joint health improves. Cognitive
            function sharpens. This is the point where you stop just fixing a
            problem and start building their future. You're laying the
            foundation for what longevity experts call a "Blue Zone"—a lifestyle
            designed for a longer, more vibrant life. Your dog doesn't just stop
            itching—they reclaim the boundless vitality they were always meant
            to have.
          </p>
        </div>
      </div>
      {/* Product Image */}
      <div className="mb-10 text-center" data-id="element-176">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8PA5e5s5iBStST9ocJFcDL/Frame_185_1.webp" alt="The Daily Foundation Chew bottle" className="rounded-lg shadow-md max-w-full md:max-w-md mx-auto" data-id="element-177" />
      </div>
      {/* Video Testimonials */}
      <div className="mb-10" data-id="element-178">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-6 text-center" data-id="element-179">
          Real Dogs. Real Results. Real Freedom from Suffering.
        </h2>
        <p className="text-lg text-center mb-8" data-id="element-180">
          See these heartfelt testimonials from dog parents who've experienced
          the transformation
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-181">
          <TestimonialCardWithImage name="Jennifer" dogName="Bailey" dogBreed="Labrador Retriever" testimonial="After 3 years of constant scratching and thousands spent at the vet, Bailey is finally free from the misery of itchy skin. She's like a new dog!" image="https://uploadthingy.s3.us-west-1.amazonaws.com/wE1YhCug9EYp357obGBdsw/topview_image_81e25acc6d9f4589a91e835c4f3d7d7e.png" date="March 15, 2023" rating={5} metrics={[{
          label: 'Reduction in scratching',
          value: '87%'
        }, {
          label: 'Elimination of hot spots',
          value: '100%'
        }]} data-id="element-182" />
          <TestimonialCardWithImage name="Michael" dogName="Tucker" dogBreed="French Bulldog" testimonial="My Frenchie was miserable with allergies. I was skeptical but desperate. Within 3 weeks, his itching stopped completely. His energy is through the roof now!" image="https://uploadthingy.s3.us-west-1.amazonaws.com/wjtBHwXUox1KLnXGvQoVGh/topview_image_fb03a32b639048b7a91889bcf712f4b4.png" date="April 2, 2023" rating={5} metrics={[{
          label: 'Improvement in coat quality',
          value: '95%'
        }, {
          label: 'Increase in energy',
          value: '3x'
        }]} data-id="element-183" />
          <TestimonialCardWithImage name="Maria" dogName="Princess" dogBreed="Shih Tzu" testimonial="My picky eater actually begs for these chews! Her constant paw licking is gone, and her groomer was shocked at how healthy her coat looks now. Best money I've ever spent." image="https://uploadthingy.s3.us-west-1.amazonaws.com/82et2b7BphMeKKMguBwTaW/topview_image_68d9dcb18c9d4daba553d7fe95210fa7.png" date="February 18, 2023" rating={5} metrics={[{
          label: 'Reduction in paw licking',
          value: '92%'
        }, {
          label: 'Improvement in appetite',
          value: '100%'
        }]} data-id="element-184" />
          <TestimonialCardWithImage name="David" dogName="Luna" dogBreed="German Shepherd" testimonial="Luna's skin allergies were so severe, we couldn't even pet her. After two months on The Daily Foundation Chew, her coat is beautiful and she's back to being our loving, playful companion." image="https://uploadthingy.s3.us-west-1.amazonaws.com/njzjzTBVUecwSBXA5DDXhm/topview_image_945ca6ad3ea847f5ba93afb1f26364ef.png" date="May 1, 2023" rating={5} metrics={[{
          label: 'Skin sensitivity reduction',
          value: '95%'
        }, {
          label: 'Money saved on treatments',
          value: '$600+'
        }]} data-id="element-185" />
        </div>
      </div>
      {/* Traditional Testimonials */}
      <div className="mb-10" data-id="element-186">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-6 text-center" data-id="element-187">
          More Success Stories from Our Community
        </h2>
        <div className="space-y-6" data-id="element-188">
          <TestimonialCard name="Jennifer M." dogName="Bailey" dogAge={7} dogBreed="Labrador" testimonial="We spent $3,000 on allergy tests and prescriptions. Nothing worked. Bailey would scratch until she bled, and I felt so helpless. Found these chews through a friend who swore by them. I was skeptical but desperate. It's been 6 months now. Bailey's coat is gorgeous, she sleeps through the night, and we haven't been to the vet for itching once. Our vet asked what we changed!" data-id="element-189" />
          <TestimonialCard name="Michael R." dogName="Tucker" dogAge={5} dogBreed="French Bulldog" testimonial="Frenchies are prone to allergies, so I thought Tucker's constant itching was just his breed. Tried Apoquel but the side effects scared me. These chews were my last resort before accepting this was our life. I cannot believe the difference. Not only did the itching stop, but his energy is through the roof. He acts like a puppy again. Worth every penny." data-id="element-190" />
          <TestimonialCard name="Maria S." dogName="Princess" dogAge={10} dogBreed="Shih Tzu" testimonial="My baby is EXTREMELY picky. She's rejected every supplement we've tried. I was sure she'd refuse these too. Not only does she eat them, she begs for them! And the results... her groomer asked if we switched foods because her coat is so shiny and healthy. No more paw licking, no more hot spots. I've already told everyone at our dog park." data-id="element-191" />
        </div>
      </div>
      {/* What We DON'T Do Section */}
      <div className="mb-10 bg-purple-50 border-4 border-purple-700 rounded-xl p-6 md:p-8 shadow-lg" data-id="element-192">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-6 text-center" data-id="element-193">
          The Difference Is in What We DON'T Do
        </h2>
        <div className="space-y-5" data-id="element-194">
          <div className="flex items-start" data-id="element-195">
            <XCircleIcon className="h-7 w-7 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-196" />
            <p className="text-lg" data-id="element-197">
              <span className="font-bold text-red-600 uppercase" data-id="element-198">We DON'T</span>{' '}
              use cheap fillers. No corn, wheat, or soy. No mysterious "meat
              meals."
            </p>
          </div>
          <div className="flex items-start" data-id="element-199">
            <XCircleIcon className="h-7 w-7 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-200" />
            <p className="text-lg" data-id="element-201">
              <span className="font-bold text-red-600 uppercase" data-id="element-202">We DON'T</span>{' '}
              cut corners on sourcing. Every ingredient is traced to its origin.
              Our organs come from grass-fed, pasture-raised cattle. Period.
            </p>
          </div>
          <div className="flex items-start" data-id="element-203">
            <XCircleIcon className="h-7 w-7 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-204" />
            <p className="text-lg" data-id="element-205">
              <span className="font-bold text-red-600 uppercase" data-id="element-206">We DON'T</span>{' '}
              hide behind proprietary blends. You'll know exactly what your dog
              is getting and why.
            </p>
          </div>
          <div className="flex items-start" data-id="element-207">
            <XCircleIcon className="h-7 w-7 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-208" />
            <p className="text-lg" data-id="element-209">
              <span className="font-bold text-red-600 uppercase" data-id="element-210">We DON'T</span>{' '}
              make empty promises. This isn't a miracle cure. It's nutrition the
              way nature intended, and the results speak for themselves.
            </p>
          </div>
        </div>
      </div>
      {/* Call to Action */}
      <div className="mb-10" data-id="element-211">
        <h2 className="text-2xl md:text-3xl font-bold text-purple-900 mb-4 text-center" data-id="element-212">
          Your Dog Has Suffered Long Enough
        </h2>
        <p className="text-lg mb-4" data-id="element-213">
          I know you're skeptical. You should be. You've been burned before by
          products that overpromised and underdelivered.
        </p>
        <p className="text-lg mb-4" data-id="element-214">
          But I also know you're desperate. Because when you love your dog like
          family, watching them suffer is unbearable.
        </p>
        <p className="text-lg mb-4" data-id="element-215">Here's what I'm offering you:</p>
        <ul className="list-none mb-6 space-y-3" data-id="element-216">
          <li className="flex items-start" data-id="element-217">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-218" />
            <span className="text-lg" data-id="element-219">
              A chance to address the ROOT CAUSE of your dog's itching, not just
              mask it.
            </span>
          </li>
          <li className="flex items-start" data-id="element-220">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-221" />
            <span className="text-lg" data-id="element-222">
              A natural solution that works WITH your dog's body, not against
              it.
            </span>
          </li>
          <li className="flex items-start" data-id="element-223">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-224" />
            <span className="text-lg" data-id="element-225">
              Freedom from the vet-pharma-supplement merry-go-round that's
              draining your wallet and breaking your heart.
            </span>
          </li>
        </ul>
        <p className="text-lg mb-6" data-id="element-226">
          The Daily Foundation Chew is available exclusively through our
          website. We don't sell on Amazon or in pet stores because we refuse to
          compromise on quality to meet their pricing demands.
        </p>
        <p className="text-lg mb-6" data-id="element-227">
          Each bottle contains a 30-day supply. Most dogs show improvement
          within 2-3 weeks, but true healing takes time. That's why we recommend
          starting with a 90-day supply to give your dog's body time to fully
          restore itself.
        </p>
        <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg mb-8" data-id="element-228">
          <h3 className="font-bold text-xl mb-4 text-center text-yellow-800" data-id="element-229">
            You Have Two Choices
          </h3>
          <div className="mb-4" data-id="element-230">
            <p className="font-bold mb-2" data-id="element-231">Choice #1:</p>
            <p className="text-lg" data-id="element-232">
              Keep doing what you're doing. Keep watching your dog scratch. Keep
              buying prescriptions with scary side effects. Keep hoping that
              maybe the next supplement will be different.
            </p>
          </div>
          <div data-id="element-233">
            <p className="font-bold mb-2" data-id="element-234">Choice #2:</p>
            <p className="text-lg" data-id="element-235">
              Try something that actually addresses the root cause. Give your
              dog the ancestral nutrients their body has been crying out for.
              Join thousands of other dog parents who've finally found the
              answer.
            </p>
          </div>
        </div>
        <p className="text-lg mb-4" data-id="element-236">
          Listen, I can't promise The Daily Foundation Chew will work for every
          dog. But I can promise this:
        </p>
        <p className="text-lg font-bold text-center mb-6" data-id="element-237">
          If you don't see a dramatic improvement in your dog's itching, energy,
          and overall vitality within 90 days, we'll refund every penny. No
          hassles. No hoops to jump through.
        </p>
        <p className="text-lg mb-8" data-id="element-238">
          Because either this transforms your dog's life, or you shouldn't pay
          for it. It's that simple.
        </p>
        <div className="bg-purple-50 p-8 rounded-lg border-l-4 border-purple-600 mb-8" data-id="element-239">
          <h3 className="text-2xl font-bold text-purple-900 mb-4" data-id="element-240">
            Your Dog Is Counting on You
          </h3>
          <p className="text-lg mb-4" data-id="element-241">
            Every day you wait is another day your dog suffers unnecessarily.
            Another night of interrupted sleep. Another day of watching them
            scratch and lick obsessively.
          </p>
          <p className="text-lg mb-4" data-id="element-242">But it doesn't have to be this way.</p>
          <p className="text-lg mb-4" data-id="element-243">
            Max is 13 now. He runs, plays, and enjoys life with the enthusiasm
            of a dog half his age. His coat is thick and shiny. He hasn't had a
            hot spot in five years.
          </p>
          <p className="text-lg mb-4" data-id="element-244">This could be your dog's story too.</p>
          <p className="text-lg mb-4" data-id="element-245">
            Click below to order The Daily Foundation Chew and give your dog the
            nutritional reboot they desperately need.
          </p>
          <p className="text-lg font-bold mb-0" data-id="element-246">
            Because they can't fix this themselves. They're counting on you to
            find the answer.
          </p>
          <p className="text-lg font-bold" data-id="element-247">And now you have.</p>
        </div>
        <div className="text-center mb-8" data-id="element-248">
          <CTAButton data-id="element-249" />
        </div>
        <div className="bg-green-50 border-2 border-green-400 p-4 rounded-lg text-center mb-8" data-id="element-250">
          <p className="text-lg font-bold text-green-800" data-id="element-251">
            90-DAY MONEY-BACK GUARANTEE
          </p>
        </div>
        <p className="text-lg mb-6" data-id="element-252">
          With love and hope for your dog's healing,
        </p>
        <p className="text-xl font-bold mb-1" data-id="element-253">Dr. Sarah Chen</p>
        <p className="text-lg italic mb-8" data-id="element-254">Founder, Snouty & Co.</p>
        <div className="border-t-2 border-gray-200 pt-6" data-id="element-255">
          <p className="text-lg font-bold" data-id="element-256">
            P.S. Remember, the itching isn't the real problem—it's just the
            symptom. While other companies sell you expensive Band-Aids, we're
            offering the cure. Your dog deserves better than a lifetime of
            medications and suffering. They deserve to thrive. Give them that
            chance today.
          </p>
        </div>
        
      </div>
    </div>;
}